import React from 'react';
import { Avatar } from '@jotpsych/ui';
import { 
  CheckCircle as CheckCircleIcon,
  ChevronRight as ChevronIcon, 
  ImportExport as SortIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Box, Checkbox, Chip, Typography, useTheme } from '@mui/material';

import type { NoteAuditListItem } from '../../../types';

/**
 * Parse patient name into first and last name
 */
function parsePatientName(patientName: string | null | undefined): {
  firstName: string;
  lastName: string;
} {
  if (!patientName) return { firstName: '', lastName: '' };

  const parts = patientName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };

  // Take first part as first name, rest as last name
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
}

interface NoteAuditTableRowProps {
  auditListItem: NoteAuditListItem;
  onClick: () => void;
  isAdminMode?: boolean;
}

export const NoteAuditTableRow: React.FC<NoteAuditTableRowProps> = ({ 
  auditListItem, 
  onClick,
  isAdminMode = false,
}) => {
  const theme = useTheme();
  const { audit, current_revision: revision } = auditListItem;

  const date = new Date(audit.last_audit_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  // In admin mode, patient_name is null - show "Hidden" instead
  const patientName = isAdminMode && !audit.patient_name ? 'Hidden' : (audit.patient_name || 'Unknown Client');
  const patientNameParts = parsePatientName(audit.patient_name);
  const hasPatient = Boolean(audit.patient_id && audit.patient_name && !isAdminMode);
  const providerName = auditListItem.provider_name || audit.provider_name || 'Unknown Clinician';
  
  // Use actual CPT data from the revision
  const cptCode = revision?.provider_cpt_codes?.[0] || null;
  const recommendedCptCode = revision?.recommended_cpt_codes?.[0] || null;
  const isCptCorrect = revision?.cpt_match ?? false;
  const score = audit.latest_compliance_pct != null ? `${audit.latest_compliance_pct.toFixed(0)}%` : '100%';

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'grid',
        gridTemplateColumns: '48px 56px 1.5fr 1fr 1fr 1fr 48px',
        alignItems: 'center',
        px: 2,
        py: 2,
        bgcolor: 'background.paper',
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
        cursor: 'pointer',
        '&:hover': {
          bgcolor: '#FBFBFF',
        },
      }}
    >
      <Box onClick={(e) => e.stopPropagation()}>
        <Checkbox size="small" />
      </Box>

      {/* Client Avatar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {hasPatient && !isAdminMode ? (
          <Avatar
            id={audit.patient_id!}
            firstName={patientNameParts.firstName}
            lastName={patientNameParts.lastName}
            sx={{
              width: 36,
              height: 36,
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          />
        ) : (
          <Box sx={{ width: 36, height: 36 }} />
        )}
      </Box>

      {/* Notes Info */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
          {date}
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          Client: {patientName}
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mt: 0.25 }}>
          Clinician: {providerName}
        </Typography>
      </Box>

      {/* Status */}
      <Box>
        <Chip
          label={audit.is_completed ? 'Complete' : 'Draft'}
          size="small"
          sx={{
            bgcolor: audit.is_completed ? '#E8F5E9' : '#F5F5F5',
            color: audit.is_completed ? '#2E7D32' : 'text.secondary',
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 24,
          }}
        />
      </Box>

      {/* CPT Code */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {cptCode ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{cptCode}</Typography>
              {isCptCorrect ? (
                <CheckCircleIcon 
                  sx={{ 
                    color: '#2E7D32', 
                    fontSize: 18,
                  }} 
                />
              ) : recommendedCptCode ? (
                <WarningIcon 
                  sx={{ 
                    color: '#FFB74D', 
                    fontSize: 18,
                  }} 
                />
              ) : null}
            </Box>
            {!isCptCorrect && recommendedCptCode && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
                  Suggested:
                </Typography>
                <Chip
                  label={recommendedCptCode}
                  size="small"
                  sx={{
                    bgcolor: theme.palette.primary.main + '15',
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 20,
                    border: `1px solid ${theme.palette.primary.main}40`,
                    '& .MuiChip-label': {
                      px: 0.75,
                    },
                  }}
                />
              </Box>
            )}
          </>
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
            —
          </Typography>
        )}
      </Box>

      {/* Score */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{score}</Typography>
      </Box>

      <Box>
        <ChevronIcon sx={{ color: theme.palette.grey[400] }} />
      </Box>
    </Box>
  );
};

export const NoteAuditTableHeader: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '48px 56px 1.5fr 1fr 1fr 1fr 48px',
        alignItems: 'center',
        px: 2,
        py: 1.5,
        bgcolor: '#F9FAFB',
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: '12px 12px 0 0',
      }}
    >
      <Box>
        <Checkbox size="small" />
      </Box>

      <Box /> {/* Avatar column - no header label */}

      <HeaderItem label="Notes" />
      <HeaderItem label="Status" />
      <HeaderItem label="CPT Code" />
      <HeaderItem label="Score" />

      <Box />
    </Box>
  );
};

const HeaderItem: React.FC<{ label: string }> = ({ label }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
        {label}
      </Typography>
      <SortIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
    </Box>
  );
};

